import type { MigrationStepExecution, StepExecutionStatus } from '../types';

/** Catégorie d'état d'un lot, agrégée depuis son statut brut. */
export type LotStatusBucket = 'pending' | 'running' | 'review' | 'failed' | 'completed';

const RUNNING_STATUSES: StepExecutionStatus[] = ['extracting', 'mapping', 'validating', 'migrating'];
const FAILED_STATUSES: StepExecutionStatus[] = [
  'extract_failed',
  'map_failed',
  'validation_failed',
  'migrate_failed',
];

/**
 * Lignes extraites que le lot n'a pas traitées : ni migrées, ni en échec, ni à
 * vérifier, ni exclues, ni ignorées. Vaut 0 sur un lot mené à son terme.
 */
export function unprocessedRecords(step: MigrationStepExecution): number {
  return Math.max(
    0,
    step.totalRecords -
      step.successRecords -
      step.failedRecords -
      step.pendingReviewRecords -
      (step.excludedRecords ?? 0) -
      (step.skippedRecords ?? 0)
  );
}

/** Réduit le statut brut d'un lot à sa catégorie d'affichage. */
export function bucketOfStep(step: MigrationStepExecution): LotStatusBucket {
  // Un lot `completed` peut garder des lignes non traitées : ré-inclure un
  // exclu (`POST /records/{id}/include`) le repasse `pending` sans sortir le
  // lot de son statut terminal, et un lot terminé ne ré-extrait pas de
  // lui-même — personne ne reprend donc cette ligne. Le lire comme « Migré »
  // affichait un lot à 0 % avec une pastille verte (EMBSE, « Configuration
  // emploi du temps », 14/08/2026) : on le remet dans « À migrer », ce qui
  // rétablit aussi le bouton de relance.
  // Idem pour un lot terminé que la V1 a rattrapé depuis : des lignes saisies
  // après le run (ECCOFOR, 04/09/2026 : EDT de la rentrée saisi en V1 après un
  // lot joué à vide) n'ont personne pour les reprendre, et un lot « Migré »
  // n'offre que « Vérifier » quand il n'est pas réconciliant. Le repasser
  // « À migrer » rétablit le bouton, dont le re-run est additif.
  if (step.status === 'completed') {
    return unprocessedRecords(step) > 0 || pendingSourceAdditions(step) > 0
      ? 'pending'
      : 'completed';
  }
  if (step.status === 'completed_with_errors') return 'review';
  if (RUNNING_STATUSES.includes(step.status)) return 'running';
  if (FAILED_STATUSES.includes(step.status)) return 'failed';
  if (step.pendingReviewRecords > 0) return 'review';
  return 'pending';
}

/**
 * Ajouts V1 en attente : lignes présentes côté V1 mais jamais extraites, donc
 * saisies APRÈS la dernière migration du lot (élève inscrit à la rentrée,
 * photo ajoutée, classe créée en août…). Alimenté par le comptage live des lots
 * utilisateurs ET de structure de classe — niveaux, classes, élèves des classes,
 * groupes (`POST /projects/{id}/source-counts`), 0 pour les autres lots.
 */
export function pendingSourceAdditions(step: MigrationStepExecution): number {
  return Math.max(0, step.newSourceRecords ?? 0);
}

/**
 * Lot « aucune donnée » : 0 ligne extraite, comptage fait — et aucune ligne
 * apparue en V1 depuis. Ce dernier garde-fou est essentiel : un lot joué avant
 * que l'école ne saisisse ses données (cas EME, réinscriptions faites après la
 * migration ; LH, élèves affectés à leurs classes en août seulement) doit
 * redevenir migrable au lieu de rester figé sur « aucune donnée », sans statut
 * ni bouton.
 */
export function isLotEmpty(step: MigrationStepExecution): boolean {
  return (
    step.totalRecords === 0 && !!step.countEstimatedAt && pendingSourceAdditions(step) === 0
  );
}

/**
 * Variante « effective » : un lot pending estimé à 0 ligne (« aucune donnée »)
 * compte comme terminé — rien à migrer. Même convention que les agrégats de
 * groupe (LotsTable) et le statut projet côté back (updateProjectTotals).
 * À utiliser pour les stats/filtres de la page projet ; `bucketOfStep` reste
 * la lecture brute du statut.
 */
export function effectiveBucketOfStep(step: MigrationStepExecution): LotStatusBucket {
  if (step.status === 'pending' && isLotEmpty(step)) {
    return 'completed';
  }
  return bucketOfStep(step);
}
